/* ANNO PER ANNO AFFIANCATO, E LE CURVE SOVRAPPOSTE.
 *
 * L'anno per anno è la parte che vale più di tutto il resto della pagina. Il
 * totale dice CHI ha reso di più; questa matrice dice QUANDO — e da lì se il
 * vantaggio è strutturale o viene da un anno solo. Il caso vero che l'ha resa
 * necessaria: un 2024 di XRP a +484% che da solo gonfiava la tenuta di
 * un'intera griglia.
 *
 * PERCHÉ NON `AnniControMercato`. Quel componente mette in fila gli anni di UN
 * backtest: è una colonna, e qui servono due o quattro colonne allineate sullo
 * stesso anno. Impilare due suoi elenchi affiancherebbe due liste che il lettore
 * dovrebbe riallineare a occhio, che è esattamente il lavoro che questa pagina
 * esiste per togliergli. Le tre convenzioni di lettura restano però le sue, alla
 * lettera, perché lo stesso anno non può risultare tinto in due modi diversi
 * dentro la stessa applicazione:
 *
 *  - il COLORE DEL TESTO dice il segno della strategia;
 *  - il GRASSETTO dice che ha battuto il mercato, e vale anche fra due numeri
 *    negativi — perdere meno è vincere;
 *  - il numero del mercato è scritto, non affidato al passaggio del mouse.
 *
 * In più, qui, un quarto segnale che là non serviva: quale candidato ha reso di
 * più in quell'anno. È blu, come tutto ciò che in questa pagina dice «questo è
 * davanti» invece di «questo guadagna».
 */

import { Fragment } from 'react';
import { Avvertenza, Pulsante } from '../../componenti';
import { num, pct } from '../../lib/formato';
import type { AnalisiAnni, Candidato, CellaAnno, SerieEquity } from './confronto';
import { TRATTI, serieEquity } from './confronto';
import './confronto.css';

/* ==========================================================================
 * LA MATRICE ANNI × CANDIDATI
 * ======================================================================== */

export function AnniAffiancati({
  candidati,
  analisi,
}: {
  candidati: Candidato[];
  analisi: AnalisiAnni;
}) {
  if (analisi.righe.length === 0) {
    return (
      <div className="sb-cf-sezione__corpo">
        <Avvertenza livello="cautela">
          Nessuno dei candidati porta i rendimenti anno per anno. Senza di essi non si può dire
          quando uno ha battuto l'altro, e nemmeno calcolare il periodo comune: restano solo i
          totali, che è la parte che questa pagina serve a non guardare da sola.
        </Avvertenza>
      </div>
    );
  }

  /* Quante colonne di numeri: serve al `min-width` della matrice. È un conto sui
     DATI — quanti candidati ci sono — non sulla larghezza dello schermo, che
     resta affare del CSS. */
  const colonne = candidati.length + (analisi.mercatoUnico ? 1 : 0);
  const larghezzaMinima = 96 + colonne * 116;

  /* Quanti anni comuni ha vinto ciascuno: il riassunto della matrice, e la
     risposta a «è stato meglio sempre o una volta sola». */
  const vinti = candidati.map(
    (c) => analisi.comuni.filter((r) => r.migliore === c.id).length,
  );

  return (
    <div className="sb-scorri-x">
      <table className="sb-cf-anni" style={{ minWidth: larghezzaMinima }}>
        <thead>
          <tr>
            <th>Anno</th>
            {candidati.map((c) => (
              <th key={c.id}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="sb-cf-lettera sb-cf-lettera--piccola">{c.lettera}</span>
                  {c.dato.strategyName}
                </span>
              </th>
            ))}
            {analisi.mercatoUnico && <th>Mercato</th>}
          </tr>
        </thead>

        <tbody>
          {analisi.righe.map((r) => (
            <tr
              key={r.anno}
              className={r.comune ? 'sb-cf-anno--comune' : 'sb-cf-anno--escluso'}
            >
              <td>
                {r.anno}
                {r.parziale && <span className="sb-cf-anno__parziale">anno parziale</span>}
                {!r.comune && r.motivo && (
                  <span className="sb-cf-anno__motivo">fuori dal periodo comune: {r.motivo}</span>
                )}
              </td>

              {candidati.map((c, i) => (
                <Cella
                  key={c.id}
                  cella={r.celle[i]}
                  vinto={r.migliore === c.id}
                  conMercato={!analisi.mercatoUnico}
                />
              ))}

              {analisi.mercatoUnico && (
                <td className="sb-cf-vuoto-cella">
                  {mercatoDellaRiga(r.celle) == null ? 'n/d' : pct(mercatoDellaRiga(r.celle) as number)}
                </td>
              )}
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr style={{ borderTop: '1px solid var(--sb-bordo-forte)' }}>
            <td>Anni in testa</td>
            {candidati.map((c, i) => (
              <td key={c.id} className="sb-cf-vuoto-cella">
                {/* Solo sugli anni comuni: contare anche quelli che un candidato
                    non ha vissuto darebbe la vittoria a chi ha misurato di più. */}
                {analisi.comuni.length === 0 ? 'n/d' : `${num(vinti[i])} / ${num(analisi.comuni.length)}`}
              </td>
            ))}
            {analisi.mercatoUnico && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Il mercato della riga quando è unico: il primo misurato vale per tutti. */
function mercatoDellaRiga(celle: CellaAnno[]): number | null {
  for (const c of celle) if (c.mercato != null) return c.mercato;
  return null;
}

function Cella({
  cella,
  vinto,
  conMercato,
}: {
  cella: CellaAnno;
  vinto: boolean;
  conMercato: boolean;
}) {
  if (cella.stato === 'fuori') {
    /* «Fuori periodo» e «non misurato» sono due assenze diverse e vanno dette
       diverse: la prima è un confine del backtest, la seconda è un dato che
       manca dove dovrebbe esserci. Uno zero al posto di entrambe sarebbe
       un'affermazione inventata. */
    return <td className="sb-cf-vuoto-cella">fuori periodo</td>;
  }
  if (cella.cambio == null) {
    return (
      <td className="sb-cf-vuoto-cella" style={{ color: 'var(--sb-cautela-testo)' }}>
        non misurato
      </td>
    );
  }

  const battuto = cella.mercato != null && cella.cambio >= cella.mercato;

  return (
    <td className={vinto ? 'sb-cf-anni__vinto' : undefined}>
      <span
        className={battuto ? 'sb-cf-anni__battuto' : undefined}
        style={{ color: cella.cambio < 0 ? 'var(--sb-perdita)' : 'var(--sb-guadagno)' }}
      >
        {pct(cella.cambio)}
      </span>
      {conMercato && (
        <span className="sb-cf-anni__mercato">
          mercato {cella.mercato == null ? 'n/d' : pct(cella.mercato)}
        </span>
      )}
    </td>
  );
}

/* ==========================================================================
 * LE CURVE DI EQUITY SOVRAPPOSTE
 * ======================================================================== */

export function CurveSovrapposte({
  candidati,
  onApri,
}: {
  candidati: Candidato[];
  /** Porta al dettaglio, dove la riesecuzione esiste già: non si duplica qui. */
  onApri: (id: number) => void;
}) {
  const { serie, senzaCurva } = serieEquity(candidati);

  return (
    <div className="sb-cf-sezione__corpo">
      {serie.length >= 1 && (
        <>
          <GraficoCurve serie={serie} />
          <div className="sb-cf-legenda">
            {serie.map((s) => {
              const t = TRATTI[indiceDi(candidati, s.id) % TRATTI.length];
              return (
                <span key={s.id} className="sb-cf-legenda__voce">
                  <span
                    className="sb-cf-legenda__tratto"
                    style={{
                      borderTopColor: t.colore,
                      borderTopStyle: t.trattini ? 'dashed' : 'solid',
                    }}
                  />
                  <span className="sb-cf-lettera sb-cf-lettera--piccola">{s.lettera}</span>
                  {nomeDi(candidati, s.id)}
                </span>
              );
            })}
          </div>
          <div className="sb-cf-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
            Ogni curva è <b>indicizzata a 100 sul proprio primo giorno</b>: i capitali iniziali
            possono differire, e una curva più alta perché è partita da più soldi non direbbe nulla
            su quale strategia sia migliore. L'asse orizzontale è il <b>tempo reale</b>, quindi due
            backtest con periodi diversi partono in due punti diversi. La scala verticale è
            logaritmica: su un +10.000% quella lineare schiaccia i primi anni contro l'asse e fa
            sembrare che non succedesse niente fino alla fine.
          </div>
        </>
      )}

      {senzaCurva.length > 0 && (
        <div style={{ marginTop: serie.length > 0 ? 'var(--sb-s-7)' : 0 }}>
          <Avvertenza livello="cautela">
            {senzaCurva.length === candidati.length
              ? 'Nessuno dei candidati porta la curva del capitale, quindi non c’è niente da sovrapporre.'
              : `Curva del capitale assente su ${senzaCurva.map((c) => `«${c.lettera}»`).join(', ')}: il grafico qui sopra mostra solo gli altri, e non è un confronto completo.`}{' '}
            Le righe nate da una griglia non salvano <code>equityLinePoints</code>: sarebbero
            megabyte a riga su migliaia di righe per gruppo. L'unico modo di averla è rieseguire
            quella configurazione da sola.
          </Avvertenza>
          <div
            style={{
              display: 'flex',
              gap: 'var(--sb-s-3)',
              flexWrap: 'wrap',
              marginTop: 'var(--sb-s-5)',
            }}
          >
            {senzaCurva.map((c) => (
              <Pulsante key={c.id} variante="tenue" onClick={() => onApri(c.id)}>
                Apri «{c.lettera}» per rieseguirlo
              </Pulsante>
            ))}
          </div>
          <div className="sb-cf-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
            La riesecuzione vive nel dettaglio, dove ci sono anche gli avvertimenti che la
            riguardano: crea un backtest <b>nuovo</b>, con un id proprio, e se le candele si sono
            estese nel frattempo i numeri possono differire di poco.
          </div>
        </div>
      )}
    </div>
  );
}

function indiceDi(candidati: Candidato[], id: number): number {
  return Math.max(0, candidati.findIndex((c) => c.id === id));
}

function nomeDi(candidati: Candidato[], id: number): string {
  const c = candidati.find((x) => x.id === id);
  return c ? `${c.dato.strategyName} · #${c.id}` : `#${id}`;
}

/**
 * Le curve, in SVG scritto a mano come gli altri grafici dell'applicazione.
 *
 * Niente libreria: porterebbe con sé un proprio sistema di colori, e qui il
 * colore è vincolato — verde e rosso hanno significato finanziario e non possono
 * dire «questa è la seconda serie». L'identità la portano la rampa del blu e il
 * tratteggio, due segnali indipendenti, più la lettera nella legenda.
 */
function GraficoCurve({ serie, altezza = 280 }: { serie: SerieEquity[]; altezza?: number }) {
  const L = 720;
  const padSinistra = 46;
  const padSotto = 26;
  const padSopra = 12;

  const tutti = serie.flatMap((s) => s.punti);
  if (tutti.length < 2) return null;

  const tMin = Math.min(...tutti.map((p) => p.t));
  const tMax = Math.max(...tutti.map((p) => p.t));
  const vMin = Math.max(0.01, Math.min(...tutti.map((p) => p.v)));
  const vMax = Math.max(vMin * 1.01, ...tutti.map((p) => p.v));

  const lMin = Math.log10(vMin);
  const lMax = Math.log10(vMax);
  const ampiezza = lMax - lMin || 1;

  const X = (t: number) =>
    padSinistra + ((t - tMin) / Math.max(1, tMax - tMin)) * (L - padSinistra - 10);
  const Y = (v: number) =>
    padSopra + (1 - (Math.log10(Math.max(v, 0.01)) - lMin) / ampiezza) * (altezza - padSopra - padSotto);

  /* Le tacche sono le potenze di dieci del moltiplicatore: su scala
     logaritmica sono le uniche equispaziate, e «×10» si legge senza calcolare. */
  const tacche: number[] = [];
  for (let e = Math.floor(lMin); e <= Math.ceil(lMax); e += 1) {
    const v = 10 ** e;
    if (v >= vMin * 0.99 && v <= vMax * 1.01) tacche.push(v);
  }

  const annoDi = (t: number) => new Date(t).getUTCFullYear();
  const anni: number[] = [];
  for (let a = annoDi(tMin); a <= annoDi(tMax); a += 1) anni.push(a);
  const passoAnni = Math.max(1, Math.ceil(anni.length / 9));

  return (
    <div className="sb-scorri-x">
      <svg
        viewBox={`0 0 ${L} ${altezza}`}
        width="100%"
        height={altezza}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: 320 }}
        role="img"
        aria-label="Curve del capitale sovrapposte, indicizzate a 100 e su scala logaritmica"
      >
        {tacche.map((v) => (
          <Fragment key={v}>
            <line
              x1={padSinistra}
              x2={L - 8}
              y1={Y(v)}
              y2={Y(v)}
              stroke="var(--sb-bordo-tenue)"
              strokeWidth={1}
            />
            <text
              x={padSinistra - 6}
              y={Y(v) + 3.5}
              textAnchor="end"
              fontSize={9}
              fontFamily="var(--sb-font-numeri)"
              fill="var(--sb-testo-tenue)"
            >
              ×{num(v / 100, v / 100 < 1 ? 2 : 0)}
            </text>
          </Fragment>
        ))}

        {anni
          .filter((_, i) => i % passoAnni === 0)
          .map((a) => {
            const t = Date.UTC(a, 0, 1);
            if (t < tMin || t > tMax) return null;
            return (
              <text
                key={a}
                x={X(t)}
                y={altezza - 8}
                textAnchor="middle"
                fontSize={9}
                fontFamily="var(--sb-font-numeri)"
                fill="var(--sb-testo-tenue)"
              >
                {a}
              </text>
            );
          })}

        <line
          x1={padSinistra}
          y1={padSopra}
          x2={padSinistra}
          y2={altezza - padSotto}
          stroke="var(--sb-bordo-forte)"
        />

        {serie.map((s, i) => {
          const t = TRATTI[i % TRATTI.length];
          const d = s.punti
            .map((p, j) => `${j ? 'L' : 'M'}${X(p.t).toFixed(1)} ${Y(p.v).toFixed(1)}`)
            .join(' ');
          return (
            <path
              key={s.id}
              d={d}
              fill="none"
              stroke={t.colore}
              strokeWidth={1.9}
              strokeDasharray={t.trattini}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </svg>
    </div>
  );
}
